import React, {
  useRef,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  StyleSheet,
  PixelRatio,
  Pressable,
  View,
  Modal,
  ActivityIndicator,
  Platform,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import BottomSheet, {
  BottomSheetBackdropProps,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import Styles from 'src/components/Styles';
import AddCard from '../../elements/rewards/AddCard';
import Localized from 'src/constants/AppStrings';
import type {CCModel} from 'src/types/CCModel';
import {useSelector} from 'react-redux';
import {RootState} from 'src/redux/store';
import ActionsFactory from 'src/actions/ActionsFactory';
import PersistentStore from 'src/services/PersistentStoreService';
import CreditCardHelper from 'src/services/CreditCardService';
import {alertError, alertSuccess} from 'src/components/helpers/AlertHelper';
import uuid from 'src/nativeModules/UUID';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import {
  initStripe,
  CardForm,
  createPaymentMethod,
} from 'src/nativeModules/Stripe';
import AVText from 'src/components/elements/AVText';
import {Modal as ModalPaper} from 'react-native-paper';
import Settings from 'src/Settings';
import NetInfo from '@react-native-community/netinfo';
import {handleBackdropPressHelper} from 'src/components/screens/rewards/policyHelper';
import {
  rewardsStartPolicyInBackground,
  rewardsCancelPolicyInBackground,
} from 'src/Util';
import KeyboardAvoidingView from 'src/components/elements/365KeyboardAvoidingView';
import KeyboardAvoidingViewAndroid from './KeyboardAvoidingViewAndroid';

export type AddCreditCardScreenProps = WithGlobalizeProps & {
  rewardProgramId: string;
};

enum BottomSheetState {
  NOT_READY = 'NOT_READY',
  FULLY_OPEN = 'FULLY_OPEN',
  FULLY_CLOSED = 'FULLY_CLOSED',
}

const RewardsAddCreditCard: React.FC<AddCreditCardScreenProps> = (props) => {
  const context = useContext(ScreenContext);
  const paymentCredentials = useSelector(
    (s: RootState) => s.account.paymentCredentials,
  );
  const creditCards = useSelector((s: RootState) => s.account.creditCards);

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [onCloseCalled, setOnCloseCalled] = useState(false);
  const [bottomSheetCurrentState, setBottomSheetCurrentState] =
    useState<BottomSheetState>(BottomSheetState.NOT_READY);
  const [modalVisible, setModalVisible] = useState(Platform.OS === 'web');
  const [optionVisible, setOptionVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [zipCode, setZipCode] = useState('');

  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [expDateError, setExpDateError] = useState<string | null>(null);
  const [cvvError, setCvvError] = useState<string | null>(null);
  const [zipCodeError, setZipCodeError] = useState<string | null>(null);

  const [defaultTokenStatus, setDefaultTokenStatus] = useState(
    creditCards.length === 0,
  );
  const [isDefaultTokenDisabled, setIsDefaultTokenDisabled] = useState(
    creditCards.length === 0,
  );

  const [isBtnActive, setIsBtnActive] = useState(false);
  const [refreshing, setIsRefreshing] = useState(false);

  const isStripe =
    paymentCredentials?.type === Settings.processorsAsString.stripe;

  const bottomSheetCustomStates = useMemo(
    () => ({
      [-1]: BottomSheetState.FULLY_CLOSED,
      0: BottomSheetState.FULLY_OPEN,
      999: BottomSheetState.NOT_READY,
    }),
    [],
  );

  useEffect(() => {
    if (!isStripe) {
      return;
    }

    if (Platform.OS === 'web') {
      NavActions.push(AppRoutes.CreditCardStripe, {
        rewardProgramId: props.rewardProgramId,
      });
    } else {
      setOptionVisible(true);
    }
  }, [isStripe, props.rewardProgramId]);

  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'AddCreditCard', {
      ...props,
    });
    if (bottomSheetRef.current) {
      bottomSheetRef.current.close();
    }
  }, [props]);

  const onClose = useCallback(() => {
    if (onCloseCalled) {
      return;
    }
    setOnCloseCalled(true);

    FirebaseAnalytic.trackEvent('onClose', 'AddCreditCard', {
      ...props,
      navigate: AppRoutes.RewardsScreen,
    });

    if (Platform.OS === 'web') {
      setModalVisible(false);
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }

    NavActions.pop();
  }, [onCloseCalled, props]);

  const handleSheetChange = (index: number) => {
    setBottomSheetCurrentState(bottomSheetCustomStates[index]);
    if (index === -1) {
      onClose();
    }
  };

  const handleBackdropPress = () => {
    if (bottomSheetCurrentState === BottomSheetState.NOT_READY) {
      NavActions.pop();
    } else if (bottomSheetRef.current) {
      bottomSheetRef.current.forceClose();
    }
  };

  const validateCardNumber = useCallback((value: string) => {
    if (!value?.length) {
      return Localized.Errors.card_number_required;
    }
    if (value.replace(/\s+/g, '').length < 15) {
      return Localized.Errors.invalid_card_number;
    }
    return null;
  }, []);

  const validateExpiry = useCallback((value: string) => {
    const m = /^(\d{2})\/(\d{2})$/.exec(value || '');
    if (!m) {
      return Localized.Errors.card_has_expired_or_invalid_date_format;
    }

    const mm = Number(m[1]);
    const yy = Number(m[2]);
    if (mm < 1 || mm > 12) {
      return Localized.Errors.card_has_expired_or_invalid_date_format;
    }

    const fullYear = 2000 + yy;
    const now = new Date();
    const expEnd = new Date(fullYear, mm, 0, 23, 59, 59, 999);

    if (expEnd < now) {
      return Localized.Errors.card_has_expired_or_invalid_date_format;
    }
    return null;
  }, []);

  const validateCvv = useCallback((value: string) => {
    if (!value?.length) {
      return Localized.Errors.security_code_required;
    }
    if (value.length < 3 || value.length > 4) {
      return Localized.Errors.invalid_security_code;
    }
    return null;
  }, []);

  const validateZip = useCallback((value: string) => {
    if (!value?.length) {
      return Localized.Errors.zip_postal_code_required;
    }
    if (value.length < 5 || value.length > 6) {
      return Localized.Errors.invalid_zip_code;
    }
    return null;
  }, []);

  const recomputeButtonActive = useCallback(() => {
    const noErrors =
      !cardNumberError && !expDateError && !cvvError && !zipCodeError;
    const allFilled = !!cardNumber && !!expiryDate && !!cvv && !!zipCode;
    setIsBtnActive(noErrors && allFilled);
  }, [
    cardNumberError,
    expDateError,
    cvvError,
    zipCodeError,
    cardNumber,
    expiryDate,
    cvv,
    zipCode,
  ]);

  useEffect(() => {
    recomputeButtonActive();
  }, [recomputeButtonActive]);

  const cardNumberAction = (text: string) => {
    setCardNumber(text);
    setCardNumberError(validateCardNumber(text));
  };

  const expMonthAction = (text: string) => {
    let v = text || '';
    v = v.replace(/[^\d]/g, '');
    if (v.length >= 3) {
      v = `${v.slice(0, 2)}/${v.slice(2, 4)}`;
    }
    if (v.length > 5) {
      v = v.slice(0, 5);
    }

    setExpiryDate(v);
    setExpDateError(validateExpiry(v));
  };

  const cvvAction = (text: string) => {
    setCvv(text);
    setCvvError(validateCvv(text));
  };

  const zipAction = (text: string) => {
    setZipCode(text);
    setZipCodeError(validateZip(text));
  };

  const onCardNumberSubmitEditing = () => {
    // no-op for now; you can wire a ref to expiry input if needed
  };

  const switchAction = (value: boolean) => {
    setDefaultTokenStatus(value);
    setIsDefaultTokenDisabled(false);
  };

  const buttonAction = () => {
    const e1 = validateCardNumber(cardNumber);
    const e2 = validateExpiry(expiryDate);
    const e3 = validateCvv(cvv);
    const e4 = validateZip(zipCode);

    setCardNumberError(e1);
    setExpDateError(e2);
    setCvvError(e3);
    setZipCodeError(e4);

    if (!e1 && !e2 && !e3 && !e4) {
      handleClick();
    }
  };

  const validateModel = (ccModel: CCModel) => {
    if (
      !ccModel.CardNumber ||
      !ccModel.ExpirationMonth ||
      !ccModel.ExpirationYear ||
      !ccModel.CvvCode ||
      !ccModel.BillingZip
    ) {
      return Localized.Errors.all_fields_required;
    }
    return null;
  };

  const handleClick = async () => {
    context?.actions?.showSpinner?.();
    if (Platform.OS === 'web') {
      setIsRefreshing(true);
    }

    const [, mmStr, yyStr] = /^(\d{2})\/(\d{2})$/.exec(expiryDate) || [];
    const expireMonth = mmStr || '';
    let expireYear = yyStr || '';
    if (expireYear && expireYear.length === 2) {
      expireYear = `20${expireYear}`;
    }

    const issuer = cardNumber ? CreditCardHelper.getCardType(cardNumber) : '';
    const accountId = (await PersistentStore.getAccountId()) || '';

    const ccModel: CCModel = {
      CardNumber: cardNumber,
      ExpirationMonth: expireMonth as unknown as number,
      ExpirationYear: expireYear as unknown as number,
      CvvCode: cvv,
      CardNumberMask: '',
      BillingZip: zipCode,
      AccountId: accountId,
      Issuer: issuer,
    };

    const errorMessage = validateModel(ccModel);
    if (errorMessage) {
      alertError(errorMessage);
      Events.Error.trackEvent(
        'Exception',
        'CreditCardScreen: validate(ccModel)',
        errorMessage,
      );
      return;
    }
    try {
      await rewardsStartPolicyInBackground(
        accountId,
        props.rewardProgramId,
        AppRoutes.RewardsAddCreditCard,
      );

      const response = await ActionsFactory.getAccountActions().addCreditCard(
        ccModel,
        paymentCredentials,
      );

      if (!response) {
        throw new Error('Invalid Token Response');
      }

      if (defaultTokenStatus) {
        await ActionsFactory.getAccountActions().updateDefaultToken(
          response.accountId,
          response.accountBalanceId,
          response.balanceTokenId,
        );
      }
      setIsRefreshing(false);
      alertSuccess(Localized.Success.credit_card_added);
      NavActions.navigate(AppRoutes.RewardsScreen, {
        refreshRewards: true,
      });
    } catch (error) {
      setModalVisible(false);
      try {
        await rewardsCancelPolicyInBackground(
          accountId,
          props.rewardProgramId,
          'Rewards Add Card cancelled due to failure',
        );
      } catch (cancelErr) {
        CrashlyticsEvents.log(
          'Exception',
          'RewardsAddCreditCardScreen:handleClick:cancelPolicyOnFailure',
          generateErrorMessage(cancelErr),
        );
        Events.Error.trackEvent(
          'Exception',
          'RewardsAddCreditCardScreen:handleClick:cancelPolicyOnFailure',
          generateErrorMessage(cancelErr),
        );
      }

      setIsRefreshing(false);
      if (error?.statusCode === 429) {
        alertError(Localized.Errors.please_try_again_shortly);
      } else {
        const guid = await uuid.getRandomUUID();
        CrashlyticsEvents.log(
          'Exception',
          'RewardsAddCreditCardScreen:handleClick',
          generateErrorMessage(error),
          guid,
        );
        Events.Error.trackEvent(
          'Exception',
          'RewardsAddCreditCardScreen:handleClick',
          generateErrorMessage(error),
          guid,
        );
        alertError(Localized.Errors.error_adding_credit_card, guid);
      }
      if (Platform.OS === 'web') {
        NavActions.pop();
      }
    } finally {
      setIsRefreshing(false);
      context?.actions?.hideSpinner?.();
    }
  };

  const onCloseStripe = () => {
    setOptionVisible(false);
    NavActions.pop();
  };

  const getTokenCard = async () => {
    try {
      const {paymentMethod: tokenCard, error} = await createPaymentMethod({
        paymentMethodType: 'Card',
      });
      if (error) {
        alertError(Localized.Errors.error_adding_credit_card, error.message);
        return;
      }
      return tokenCard;
    } catch {
      return;
    }
  };

  const addCardStripe = async () => {
    const accountId = (await PersistentStore.getAccountId()) || '';
    const state = await NetInfo.fetch();
    if (!state.isConnected) {
      alertError(Localized.Errors.add_credit_network_error);
      return;
    }

    let rewardStarted = false;
    try {
      setLoading(true);

      await rewardsStartPolicyInBackground(
        accountId,
        props.rewardProgramId,
        AppRoutes.RewardsAddCreditCard,
      );
      rewardStarted = true;

      await initStripe({publishableKey: paymentCredentials?.key});
      const setupIntent =
        await ActionsFactory.getAccountActions().createSetupIntent(accountId);

      if (!setupIntent?.clientSecret) {
        try {
          if (rewardStarted) {
            await rewardsCancelPolicyInBackground(
              accountId,
              props.rewardProgramId,
              'Rewards Add Card (Stripe) cancelled: no client secret',
            );
          }
        } catch {}
        alertError(Localized.Errors.error_adding_credit_card);
        setLoading(false);
        return;
      }

      const token = await getTokenCard();
      if (!token) {
        try {
          if (rewardStarted) {
            await rewardsCancelPolicyInBackground(
              accountId,
              props.rewardProgramId,
              'Rewards Add Card (Stripe) cancelled: token not created',
            );
          }
        } catch {}
        setLoading(false);
        return;
      }

      const ccModel: CCModel = {
        CardNumberMask: `x${token.Card.last4}`,
        Token: token.id,
        Provider: Settings.processors.stripe,
        AccountId: accountId,
        BillingZip: token.billingDetails.address.postalCode,
        CardNumber: '',
        CvvCode: '',
        ExpirationMonth: token.Card.expMonth,
        ExpirationYear: token.Card.expYear,
        Issuer: token.Card.brand,
      };

      await ActionsFactory.getAccountActions().addCreditCardTokenStripe(
        ccModel,
      );
      alertSuccess(Localized.Success.credit_card_added);
      setOptionVisible(false);
      NavActions.navigate(AppRoutes.RewardsScreen, {
        refreshRewards: true,
      });
    } catch (error) {
      try {
        if (rewardStarted) {
          await rewardsCancelPolicyInBackground(
            accountId,
            props.rewardProgramId,
            'Rewards Add Card (Stripe) cancelled due to failure',
          );
        }
      } catch (cancelErr) {
        const guid = await uuid.getRandomUUID();
        CrashlyticsEvents.log(
          'Exception',
          'RewardsAddCreditCard:addCardStripe:cancelPolicyOnFailure',
          generateErrorMessage(cancelErr),
          guid,
        );
        Events.Error.trackEvent(
          'Exception',
          'RewardsAddCreditCard:addCardStripe:cancelPolicyOnFailure',
          generateErrorMessage(cancelErr),
          guid,
        );
      }
      const guid = await uuid.getRandomUUID();
      CrashlyticsEvents.log(
        'Exception',
        'RewardsAddCreditCard:addCardStripe',
        generateErrorMessage(error),
        guid,
      );
      Events.Error.trackEvent(
        'Exception',
        'RewardsAddCreditCard:addCardStripe',
        generateErrorMessage(error),
        guid,
      );
      alertError(Localized.Errors.error_adding_credit_card, guid);
    } finally {
      setLoading(false);
    }
  };

  const handlePress = React.useCallback(() => {
    handleBackdropPressHelper({onClose, bottomSheetRef});
  }, [onClose]);

  const renderBackdropComponent = (backDropProps: BottomSheetBackdropProps) => (
    <Pressable style={backDropProps.style} onPress={handleBackdropPress} />
  );

  const renderBottomSheet = () => (
    <BottomSheet
      ref={bottomSheetRef}
      snapPoints={[490 + PixelRatio.getFontScale() * 35]}
      enableHandlePanningGesture={true}
      onChange={handleSheetChange}
      enablePanDownToClose={true}
      backdropComponent={renderBackdropComponent}
      style={styles.bottomSheet}
    >
      <BottomSheetScrollView keyboardShouldPersistTaps="handled">
        <AddCard
          cardNumber={cardNumber}
          expiryDate={expiryDate}
          cvv={cvv}
          zipCode={zipCode}
          cardNumberError={cardNumberError || undefined}
          expDateError={expDateError || undefined}
          cvvError={cvvError || undefined}
          zipCodeError={zipCodeError || undefined}
          cardNumberAction={cardNumberAction}
          expMonthAction={expMonthAction}
          cvvAction={cvvAction}
          zipAction={zipAction}
          onCardNumberSubmitEditing={onCardNumberSubmitEditing}
          defaultTokenStatus={defaultTokenStatus}
          switchAction={switchAction}
          buttonAction={buttonAction}
          _isBtnActive={isBtnActive}
          isDefaultTokenDisabled={isDefaultTokenDisabled}
        />
      </BottomSheetScrollView>
    </BottomSheet>
  );

  const renderModal = () => (
    <Modal
      animationType="fade"
      transparent
      visible={modalVisible}
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>
          <AVTouchableOpacity
            aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} offer details`}
            role="button"
            style={styles.closeModalContainer}
            onPress={onClose}
            accessible
            accessibilityLabel={Localized.Buttons.cancel}
            accessibilityRole="button"
            hitSlop={Styles.HitSlop_10}
          >
            <View style={styles.closeModalTitleContainer}>
              <CancelIcon />
            </View>
          </AVTouchableOpacity>
          {refreshing ? (
            <View style={styles.loader}>
              <ActivityIndicator />
            </View>
          ) : (
            <AddCard
              cardNumber={cardNumber}
              expiryDate={expiryDate}
              cvv={cvv}
              zipCode={zipCode}
              cardNumberError={cardNumberError || undefined}
              expDateError={expDateError || undefined}
              cvvError={cvvError || undefined}
              zipCodeError={zipCodeError || undefined}
              cardNumberAction={cardNumberAction}
              expMonthAction={expMonthAction}
              cvvAction={cvvAction}
              zipAction={zipAction}
              onCardNumberSubmitEditing={onCardNumberSubmitEditing}
              defaultTokenStatus={defaultTokenStatus}
              switchAction={switchAction}
              buttonAction={buttonAction}
              _isBtnActive={isBtnActive}
              isDefaultTokenDisabled={isDefaultTokenDisabled}
            />
          )}
        </View>
      </View>
    </Modal>
  );

  const renderAddCardModal = () => {
    if (!isStripe || Platform.OS === 'web') {
      return null;
    }

    return (
      <ModalPaper
        onDismiss={onCloseStripe}
        visible={optionVisible}
        style={Platform.select({android: {justifyContent: 'flex-end'}})}
      >
        <View style={styles.stripeModalContainer}>
          <View style={styles.subModalContainer}>
            <View style={styles.titleContainer}>
              <Pressable
                accessible
                accessibilityLabel={Localized.Buttons.cancel}
                nativeID={'Btn' + Localized.Buttons.cancel}
                accessibilityRole="button"
                aria-label={Localized.Buttons.cancel}
                role="button"
                onPress={onCloseStripe}
              >
                <AVText style={styles.cancelText}>
                  {Localized.Buttons.cancel}
                </AVText>
              </Pressable>

              <AVText
                accessible
                accessibilityLabel={Localized.Labels.add_card}
                accessibilityRole="text"
                aria-label={`${Localized.Labels.add_card}, text`}
                style={styles.boldText}
              >
                {Localized.Labels.add_card}
              </AVText>

              {loading ? (
                <View
                  accessible
                  accessibilityState={{busy: loading}}
                  aria-busy={loading}
                >
                  <ActivityIndicator />
                </View>
              ) : (
                <Pressable
                  accessible
                  accessibilityLabel={Localized.Buttons.done}
                  accessibilityRole="button"
                  accessibilityHint="Double tap to finish adding a new credit card"
                  aria-label={Localized.Buttons.done}
                  role="button"
                  onPress={addCardStripe}
                  testID="qa_Done_btn"
                  nativeID="qa_Done_btn"
                >
                  <AVText style={styles.boldText}>
                    {Localized.Buttons.done}
                  </AVText>
                </Pressable>
              )}
            </View>

            <CardForm
              autofocus
              style={styles.cardForm}
              cardStyle={styles.cardStyle}
            />
          </View>
        </View>
      </ModalPaper>
    );
  };

  const KeyBoardAvoiding =
    Platform.OS === 'ios' ? KeyboardAvoidingView : KeyboardAvoidingViewAndroid;

  return (
    <View style={styles.container}>
      <KeyBoardAvoiding style={Styles.Style.flex} behavior="padding" insideTab>
        <Pressable style={styles.backdropPressable} onPress={handlePress} />
        {isStripe
          ? renderAddCardModal()
          : Platform.OS === 'web'
          ? renderModal()
          : renderBottomSheet()}
      </KeyBoardAvoiding>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.bottomTreyBackgroundColor,
  },
  backdropPressable: {
    width: '100%',
    minHeight: '60%',
    backgroundColor: 'transparent',
  },
  bottomSheet: {
    borderRadius: Styles.Spacing.m4,
    overflow: 'hidden',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Styles.Spacing.m3,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 600,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m3 + Styles.Spacing.m2,
    overflow: 'hidden',
    padding: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  closeModalContainer: {
    position: 'absolute',
    top: Styles.Spacing.m1,
    right: Styles.Spacing.m1,
    width: Styles.Heights.touchTargetHeight2,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeModalTitleContainer: {
    width: Styles.Spacing.m2 + Styles.Spacing.m3,
    height: Styles.Spacing.m2 + Styles.Spacing.m3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  subModalContainer: {
    backgroundColor: Styles.addCardPopupColor,
    paddingHorizontal: Styles.Spacing.m2,
    paddingVertical: Styles.Spacing.m3,
    borderRadius: Styles.Spacing.m2,
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cancelText: {
    color: Styles.primaryColor,
  },
  boldText: {
    fontWeight: 'bold',
    color: Styles.darkColor,
  },
  cardForm: {
    marginTop: Styles.Spacing.m3,
    height: Platform.OS === 'ios' ? 180 : 300,
  },
  cardStyle: {
    backgroundColor: Styles.addCardPopupColor,
  },
  stripeModalContainer: {
    alignSelf: 'center',
    width: Styles.ScreenWidth / 1.1,
    borderRadius: 10,
    marginBottom: Styles.Spacing.m3,
  },
  loader: {minHeight: '60%'},
});

export default withForwardedNavigationParams()(
  withGlobalize(RewardsAddCreditCard),
);
